import { memo, useCallback, useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getApiRequest } from "../../../services/getApiRequest";
import { END_POINTS } from "../../../Helper/Constant";
import { showToast } from "../ToasterMessage/ToasterMessage";
import goatIcon from "../../../assets/icons/full-goat.png";
import coinIcon from "../../../assets/icons/header-coin.svg";
import "./SendGiftTypeModal.scss";

const GIFT_ICONS = {
  goat: goatIcon,
};

const isVideoUrl = (url) => /\.(mp4|webm|ogg)(\?|#|$)/i.test(url || "");

const getGiftIdentity = (gift) => {
  const slug = String(gift?.slug || "").toLowerCase();
  const rawName = gift?.name;
  const name =
    typeof rawName === "string"
      ? rawName
      : `${rawName?.en || ""} ${rawName?.ar || ""}`;
  return `${slug} ${name}`.toLowerCase();
};

const getGoatClownDescriptionKey = (gift) => {
  const identity = getGiftIdentity(gift);
  if (identity.includes("goat") || identity.includes("قوت")) return "gift.goat_description";
  if (identity.includes("clown") || identity.includes("مهرج")) return "gift.clown_description";
  return null;
};

const parseGiftTypes = (response) => {
  if (Array.isArray(response?.giftTypes)) return response.giftTypes;
  if (Array.isArray(response?.gifts)) return response.gifts;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const SendGiftTypeModal = ({ open, onClose, category, receiverId }) => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const [giftTypes, setGiftTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const getGiftName = useCallback(
    (name) => {
      if (!name) return "";
      if (typeof name === "string") return name;
      return i18n.language === "ar" ? name.ar || name.en : name.en || name.ar;
    },
    [i18n.language]
  );

  useEffect(() => {
    if (!open || !category) {
      setGiftTypes([]);
      return;
    }

    const fetchGiftTypes = async () => {
      setLoading(true);
      try {
        const response = await getApiRequest(
          `${END_POINTS.GET_GIFT_TYPES}?category=${category}`
        );
        if (response?.success) {
          setGiftTypes(parseGiftTypes(response));
        } else {
          setGiftTypes([]);
        }
      } catch (error) {
        setGiftTypes([]);
        showToast(error?.response?.data?.message || error?.error?.message, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchGiftTypes();
  }, [open, category]);

  const handleSelectGift = useCallback(
    (gift) => {
      if (!receiverId || !gift?._id) return;
      onClose();
      const params = new URLSearchParams({ giftTypeId: gift._id });
      if (category) params.set("category", category);
      navigate(`/send-gift/${receiverId}?${params.toString()}`);
    },
    [receiverId, category, navigate, onClose]
  );

  const title =
    category === "roast"
      ? t("user_profile.send_roast", "Send Roast")
      : t("user_profile.send_hype", "Send Hype");

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      width={640}
      className={`send-gift-type-modal send-gift-type-modal--${category || "gift"}`}
      title={title}
    >
      <Spin spinning={loading}>
        {giftTypes.length > 0 ? (
          <div className="send-gift-type-grid">
            {giftTypes.map((gift) => {
              const giftName = getGiftName(gift?.name);
              const mediaUrl = gift?.mediaUrl || "";
              const isVideo = isVideoUrl(mediaUrl);
              const image =
                gift?.thumbnailUrl ||
                mediaUrl ||
                GIFT_ICONS[gift?.slug] ||
                goatIcon;

              const descriptionKey = getGoatClownDescriptionKey(gift);

              return (
                <button
                  type="button"
                  className="send-gift-type-card"
                  key={gift?._id || gift?.slug}
                  onClick={() => handleSelectGift(gift)}
                >
                  <span className="send-gift-type-card__media">
                    {isVideo ? (
                      <video
                        className="send-gift-type-card__img"
                        src={mediaUrl}
                        poster={gift?.thumbnailUrl || undefined}
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <img
                        src={image}
                        alt={giftName}
                        className="send-gift-type-card__img"
                        loading="lazy"
                      />
                    )}
                  </span>
                  {descriptionKey && (
                    <p className="send-gift-type-card__desc">
                      {t(
                        descriptionKey,
                        descriptionKey === "gift.goat_description"
                          ? "Once GOAT is received by a player the whole profile acquires THE GOAT features"
                          : "Once Clown is received by a player the whole profile acquires THE CLOWN features"
                      )}
                    </p>
                  )}
                  {giftName && (
                    <p className="send-gift-type-card__name">{giftName}</p>
                  )}
                  <span className="send-gift-type-card__price">
                    <img src={coinIcon} alt="" loading="lazy" />
                    {Number(gift?.price || 0).toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          !loading && (
            <p className="send-gift-type-empty">
              {t("user_profile.no_gifts_found", "No gifts found.")}
            </p>
          )
        )}
      </Spin>
    </Modal>
  );
};

SendGiftTypeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  category: PropTypes.oneOf(["roast", "hype"]),
  receiverId: PropTypes.string,
};

export default memo(SendGiftTypeModal);
